import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  BrainCircuit, Sparkles, Send, DollarSign, TrendingUp, Target, ShoppingCart,
  Users, Percent, AlertTriangle, Info, CheckCircle2, X, RefreshCw, Plug, Plus,
  BookOpen, Save, ArrowRight, Radio, Gauge, ShieldCheck,
} from 'lucide-react'
import { getSession } from '../lib/session'
import BrainConstellation from '../components/BrainConstellation'
import MarketingDashboards from '../components/MarketingDashboards'

// Marketing Intelligence Brain — one place to ask your marketing anything and get an
// evidence-first answer: the number, where it came from, how confident we are, and what
// to do next. Below the ask sits a live briefing, a signal feed, and the connectors +
// plain-language metric definitions that feed the whole thing.
const API = '/api/v1/marketing-intel'

// ---- small format helpers -------------------------------------------------
const fmtDate = (d) => d.toISOString().slice(0, 10) // YYYY-MM-DD
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d }
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v)
const money = (v) => isNum(v) ? `$${Math.round(v).toLocaleString()}` : (v ?? '—')
const num = (v) => isNum(v) ? v.toLocaleString() : (v ?? '—')
const ratio = (v) => isNum(v) ? `${v.toFixed(2)}×` : (v ?? '—')

const RANGES = [['7d', 7], ['30d', 30], ['90d', 90]]

// Tab bar: only one section shows at a time so the page reads calmly.
const TABS = [
  { id: 'Overview', label: 'Overview', icon: BrainCircuit },
  { id: 'Briefings', label: 'Briefings', icon: Gauge },
  { id: 'Signals', label: 'Signals', icon: Radio },
]
const TAB_STORE_KEY = 'marketingBrain.activeTab'

const EXAMPLE_QUESTIONS = [
  'Where is our budget producing revenue?',
  'Why did sales drop even though traffic increased?',
  'Which SEO pages create pipeline?',
  'What is our blended CAC this month?',
]

// Severity → colour tokens for the signal feed and confidence pills.
const SEV = {
  critical: { pill: 'bg-red-500/10 text-red-600', dot: 'bg-red-500', icon: AlertTriangle, ring: 'border-red-600/30' },
  high:     { pill: 'bg-red-500/10 text-red-600', dot: 'bg-red-500', icon: AlertTriangle, ring: 'border-red-600/30' },
  warning:  { pill: 'bg-amber-500/10 text-amber-600', dot: 'bg-amber-500', icon: AlertTriangle, ring: 'border-amber-500/30' },
  warn:     { pill: 'bg-amber-500/10 text-amber-600', dot: 'bg-amber-500', icon: AlertTriangle, ring: 'border-amber-500/30' },
  success:  { pill: 'bg-green-500/10 text-green-600', dot: 'bg-green-500', icon: CheckCircle2, ring: 'border-green-600/30' },
  good:     { pill: 'bg-green-500/10 text-green-600', dot: 'bg-green-500', icon: CheckCircle2, ring: 'border-green-600/30' },
  info:     { pill: 'bg-blue-500/10 text-blue-600', dot: 'bg-blue-400', icon: Info, ring: 'border-blue-500/30' },
}
const sev = (s) => SEV[String(s || 'info').toLowerCase()] || SEV.info

export default function MarketingBrain() {
  const session = getSession()
  const [range, setRange] = useState(30)
  const [tab, setTab] = useState(() => {
    try {
      const saved = localStorage.getItem(TAB_STORE_KEY)
      return TABS.some(t => t.id === saved) ? saved : 'Overview'
    } catch { return 'Overview' }
  })

  const selectTab = (id) => {
    setTab(id)
    try { localStorage.setItem(TAB_STORE_KEY, id) } catch { /* ignore */ }
  }

  const to = fmtDate(new Date())
  const from = fmtDate(daysAgo(range))

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BrainCircuit size={24} className="text-primary-600" /> Marketing Brain
          </h2>
          <p className="text-gray-500 mt-1">Ask your marketing anything — get the number, the source, and the next move.</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1">
          {RANGES.map(([label, days]) => (
            <button key={days} onClick={() => setRange(days)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                range === days ? 'bg-primary-600 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}>{label}</button>
          ))}
        </div>
      </div>

      {!session?.namespaceId && (
        <div className="card text-center py-12">
          <BrainCircuit size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Connect a workspace to activate your Marketing Brain.</p>
        </div>
      )}

      {session?.namespaceId && (
        <>
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1 w-fit">
            {TABS.map(t => (
              <button key={t.id} onClick={() => selectTab(t.id)}
                className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  tab === t.id ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`}>
                <t.icon size={15} /> {t.label}
              </button>
            ))}
          </div>

          {tab === 'Overview' && <BrainConstellation />}
          {tab === 'Briefings' && <MarketingDashboards from={from} to={to} />}
          {tab === 'Signals' && <Signals />}
        </>
      )}
    </div>
  )
}

// ---- ASK ------------------------------------------------------------------
function AskPanel({ from, to }) {
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [answer, setAnswer] = useState(null)

  const ask = async (question) => {
    const text = (question ?? q).trim()
    if (!text) { toast.error('Ask a question first'); return }
    setQ(text)
    setBusy(true)
    try {
      const r = await axios.post(`${API}/ask`, { question: text, from, to })
      setAnswer(r.data || {})
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not answer that yet')
    } finally { setBusy(false) }
  }

  return (
    <div className="card bg-gradient-to-br from-primary-500/5 to-transparent border-primary-600/20">
      <label className="block">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
          <Sparkles size={16} className="text-primary-600" /> Ask your marketing anything
        </span>
        <div className="flex gap-2">
          <input className="input" value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && ask()} placeholder="Where is our budget producing revenue?" />
          <button onClick={() => ask()} disabled={busy}
            className="btn-primary text-sm flex items-center gap-2 whitespace-nowrap disabled:opacity-60">
            {busy ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />} Ask
          </button>
        </div>
      </label>

      <div className="flex flex-wrap gap-2 mt-3">
        {EXAMPLE_QUESTIONS.map(ex => (
          <button key={ex} onClick={() => ask(ex)} disabled={busy}
            className="text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 hover:border-primary-600/40 hover:text-primary-600 transition-colors disabled:opacity-60">
            {ex}
          </button>
        ))}
      </div>

      {busy && !answer && <div className="mt-5 text-center text-gray-400 py-6">Thinking through your data…</div>}
      {answer && <AnswerCard answer={answer} loading={busy} />}
    </div>
  )
}

function AnswerCard({ answer, loading }) {
  const metrics = Array.isArray(answer.metrics) ? answer.metrics : []
  const sources = (Array.isArray(answer.sources) ? answer.sources : [])
    .map(s => (typeof s === 'string' ? s : (s?.provider || s?.label || ''))).filter(Boolean)
  const actions = (Array.isArray(answer.actions) ? answer.actions : []).map(a =>
    typeof a === 'string'
      ? { title: a }
      : { title: a?.title || a?.label || a?.action || '', detail: a?.detail || a?.body || a?.rationale || '' }
  ).filter(a => a.title)
  const conf = isNum(answer.confidence)
    ? (answer.confidence <= 1 ? Math.round(answer.confidence * 100) : Math.round(answer.confidence))
    : null
  const confTone = conf == null ? sev('info') : conf >= 75 ? sev('success') : conf >= 50 ? sev('warning') : sev('info')
  const baseline = answer.baseline

  return (
    <div className={`mt-5 rounded-xl border border-gray-200 bg-gray-50 p-5 ${loading ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-lg font-semibold text-gray-900 leading-snug flex-1 min-w-[240px]">
          {answer.answer || answer.summary || 'No answer returned.'}
        </p>
        {conf != null && (
          <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${confTone.pill}`}>
            <Gauge size={13} /> {conf}% confident
          </span>
        )}
      </div>

      {metrics.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {metrics.map((m, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-gray-100 p-3">
              <p className="text-xs text-gray-500">{m.label}</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5 tabular-nums">
                {isNum(m.value) ? m.value.toLocaleString() : (m.value ?? '—')}
              </p>
              {m.period && <p className="text-[11px] text-gray-400 mt-0.5">{m.period}</p>}
            </div>
          ))}
        </div>
      )}

      {baseline != null && baseline !== '' && (
        <p className="text-sm text-gray-500 mt-3">
          <span className="font-medium text-gray-600">Baseline:</span>{' '}
          {typeof baseline === 'object'
            ? `${baseline.label || 'prior period'} ${baseline.value != null ? '· ' + (isNum(baseline.value) ? baseline.value.toLocaleString() : baseline.value) : ''}`
            : String(baseline)}
        </p>
      )}

      {sources.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-gray-400">Sources:</span>
          {sources.map(s => (
            <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-600 border border-primary-600/20 capitalize">{s}</span>
          ))}
        </div>
      )}

      {actions.length > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Recommended actions</p>
          <ol className="space-y-2">
            {actions.map((a, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-primary-600 text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{a.title}</p>
                  {a.detail && <p className="text-xs text-gray-500 mt-0.5">{a.detail}</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

// ---- BRIEFING -------------------------------------------------------------
function Briefing({ from, to }) {
  const [s, setS] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    axios.get(`${API}/summary`, { params: { from, to } })
      .then(r => setS(r.data || {}))
      .catch(() => setS({}))
      .finally(() => setLoading(false))
  }, [from, to])

  const tiles = [
    { label: 'Spend', value: money(s?.spend), icon: DollarSign, color: 'text-primary-600' },
    { label: 'Revenue', value: money(s?.revenue), icon: TrendingUp, color: 'text-green-600' },
    { label: 'ROAS', value: ratio(s?.roas), icon: Percent, color: 'text-blue-600' },
    { label: 'CAC', value: money(s?.cac), icon: Target, color: 'text-amber-600' },
    { label: 'Orders', value: num(s?.orders), icon: ShoppingCart, color: 'text-gray-500' },
    { label: 'Leads', value: num(s?.leads), icon: Users, color: 'text-gray-500' },
  ]

  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-3">Briefing</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {tiles.map((t, i) => (
          <div key={i} className="card p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">{t.label}</p>
              <t.icon size={16} className={t.color} />
            </div>
            <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">{loading ? '…' : t.value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- SIGNALS --------------------------------------------------------------
function Signals() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState('')

  const load = () => {
    setLoading(true)
    axios.get(`${API}/insights`, { params: { kind: 'signal', status: 'open' } })
      .then(r => setItems(r.data.insights || r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const resolve = async (it) => {
    setBusyId(it.id)
    setItems(list => list.filter(x => x.id !== it.id)) // optimistic
    try { await axios.post(`${API}/insights/${it.id}/resolve`) }
    catch (err) { toast.error(err.response?.data?.error || 'Could not resolve'); load() }
    finally { setBusyId('') }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-2"><Radio size={14} /> Signals</h3>
        <button onClick={load} className="text-xs text-gray-400 hover:text-primary-600 flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
      </div>

      {loading && items.length === 0 && <div className="card text-center text-gray-400 py-8">Scanning for signals…</div>}
      {!loading && items.length === 0 && (
        <div className="card text-center py-10">
          <CheckCircle2 size={28} className="mx-auto text-green-500/70 mb-2" />
          <p className="text-gray-500 text-sm">No open signals. Your Brain is watching for anomalies.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(it => {
          const c = sev(it.severity)
          return (
            <div key={it.id} className={`card p-4 border-l-4 ${c.ring}`} style={{ borderLeftColor: 'currentColor' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3 min-w-0">
                  <span className={`mt-0.5 shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${c.pill}`}><c.icon size={16} /></span>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{it.title}</p>
                    {it.body && <p className="text-sm text-gray-500 mt-0.5">{it.body}</p>}
                    {it.question && <p className="text-xs text-primary-600 mt-1 flex items-center gap-1"><Sparkles size={11} /> {it.question}</p>}
                    {Array.isArray(it.actions) && it.actions.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {it.actions.map((a, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-center gap-1.5">
                            <ArrowRight size={11} className="text-gray-400" /> {typeof a === 'string' ? a : (a?.title || a?.label || '')}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${c.pill}`}>{it.severity || 'info'}</span>
                  <button onClick={() => resolve(it)} disabled={busyId === it.id}
                    className="text-xs text-gray-400 hover:text-green-600 flex items-center gap-1 disabled:opacity-60">
                    <CheckCircle2 size={13} /> Resolve
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- CONNECTORS + DEFINITIONS ---------------------------------------------
// Internal providers we can wire up with one click; external ones need OAuth.
const ONE_CLICK = new Set(['internal', 'stripe'])

function Connectors() {
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState('')

  const load = () => {
    setLoading(true)
    axios.get(`${API}/providers`)
      .then(r => setProviders(r.data.providers || r.data.items || []))
      .catch(() => setProviders([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const connect = async (p) => {
    if (!ONE_CLICK.has(p.provider)) { toast('OAuth coming soon for ' + (p.label || p.provider)); return }
    setBusy(p.provider)
    try {
      await axios.post(`${API}/connectors`, { provider: p.provider, label: p.label })
      toast.success(`${p.label || p.provider} connected`)
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Could not connect') }
    finally { setBusy('') }
  }

  const sync = async (p) => {
    setBusy(p.provider)
    try {
      const r = await axios.post(`${API}/connectors/${p.provider}/sync`)
      toast.success(r.data?.message || `Synced ${p.label || p.provider}`)
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Sync failed') }
    finally { setBusy('') }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-2"><Plug size={14} /> Connectors</h3>
        {loading && providers.length === 0 && <div className="card text-center text-gray-400 py-8">Loading connectors…</div>}
        {!loading && providers.length === 0 && (
          <div className="card text-center py-8 text-sm text-gray-500">No providers available yet.</div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {providers.map(p => {
            const connected = p.connected ?? (p.status === 'connected')
            const external = !ONE_CLICK.has(p.provider)
            return (
              <div key={p.provider} className="card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 capitalize">{p.label || p.provider}</p>
                    {p.category && <p className="text-[11px] text-gray-400 capitalize">{p.category}</p>}
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${connected ? 'bg-green-500/10 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                    {connected ? 'Connected' : 'Not connected'}
                  </span>
                </div>
                {p.last_sync_at && <p className="text-[11px] text-gray-400 mt-2">Synced {new Date(p.last_sync_at).toLocaleDateString()}</p>}
                {p.error && <p className="text-[11px] text-red-500 mt-1 truncate" title={p.error}>{p.error}</p>}
                <div className="mt-3 flex gap-2">
                  {connected ? (
                    <button onClick={() => sync(p)} disabled={busy === p.provider}
                      className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-60">
                      <RefreshCw size={13} className={busy === p.provider ? 'animate-spin' : ''} /> Sync
                    </button>
                  ) : external ? (
                    <span className="text-xs text-gray-400 flex items-center gap-1.5 px-1 py-1.5"><Plus size={13} /> OAuth coming soon</span>
                  ) : (
                    <button onClick={() => connect(p)} disabled={busy === p.provider}
                      className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-60">
                      <Plus size={13} /> Connect
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <Definitions />
    </div>
  )
}

function Definitions() {
  const [defs, setDefs] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [savingKey, setSavingKey] = useState('')

  const load = () => {
    setLoading(true)
    axios.get(`${API}/definitions`)
      .then(r => setDefs(r.data.definitions || r.data.items || []))
      .catch(() => setDefs([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const seed = async () => {
    setBusy(true)
    try {
      await axios.post(`${API}/definitions/seed`)
      toast.success('Seeded default definitions')
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Could not seed definitions') }
    finally { setBusy(false) }
  }

  const edit = (key, field, value) => setDefs(list => list.map(d => d.key === key ? { ...d, [field]: value } : d))

  const save = async (d) => {
    setSavingKey(d.key)
    try {
      await axios.put(`${API}/definitions`, { key: d.key, label: d.label, definition: d.definition, formula: d.formula || {} })
      toast.success(`Saved “${d.label || d.key}”`)
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save definition') }
    finally { setSavingKey('') }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-2"><BookOpen size={14} /> Definitions</h3>
        <button onClick={seed} disabled={busy}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-60">
          <Sparkles size={12} /> {busy ? 'Seeding…' : 'Seed definitions'}
        </button>
      </div>

      {loading && defs.length === 0 && <div className="card text-center text-gray-400 py-8">Loading…</div>}
      {!loading && defs.length === 0 && (
        <div className="card text-center py-8">
          <BookOpen size={26} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No metric definitions yet.</p>
          <button onClick={seed} disabled={busy} className="btn-primary text-xs mt-3 disabled:opacity-60">Seed defaults</button>
        </div>
      )}

      <div className="space-y-3">
        {defs.map(d => (
          <div key={d.key} className="card p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <input className="input font-semibold" value={d.label || ''} onChange={e => edit(d.key, 'label', e.target.value)} placeholder={d.key} />
              <span className="text-[10px] font-mono text-gray-400 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 shrink-0">{d.key}</span>
            </div>
            <textarea className="input text-sm" rows={2} value={d.definition || ''}
              onChange={e => edit(d.key, 'definition', e.target.value)} placeholder="Plain-language definition…" />
            <div className="flex justify-end mt-2">
              <button onClick={() => save(d)} disabled={savingKey === d.key}
                className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-60">
                <Save size={13} /> {savingKey === d.key ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {defs.length > 0 && (
        <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1"><ShieldCheck size={12} /> Definitions ground every answer — edit them so the Brain speaks your language.</p>
      )}
    </div>
  )
}
